import moment from 'moment/moment';

export class ActiveStateService {
  constructor(public app: any) {}

  public check(element: any, now = new Date()) {
    if (element?.content?.finished) return 'finished';
    if (!element?.published) return 'disabled';
    if (!this.weekdaysValid(element?.weekdaysInterval)) return 'paused';

    element.publishStart = this.cleanUpDate(element?.publishStart);
    element.publishEnd = this.cleanUpDate(element?.publishEnd);

    const getT = (d: any) => d?.getTime ? d.getTime() : d;
    const startT = getT(element.publishStart);
    const endT = getT(element.publishEnd);
    const nowT = now.getTime();

    if ((startT && startT > nowT) || (endT && endT < nowT)) {
      return 'paused';
    }

    if (element.publishStartTime && this.sameDay(now, element.publishStart)) {
      const [h, m] = element.publishStartTime.split(':').map(Number);
      if (h > now.getHours() || (h === now.getHours() && m > now.getMinutes())) {
        return 'paused';
      }
    }
    if (element.publishEndTime && this.sameDay(now, element.publishEnd)) {
      const [h, m] = element.publishEndTime.split(':').map(Number);
      if (h < now.getHours() || (h === now.getHours() && m < now.getMinutes())) {
        return 'paused';
      }
    }

    if (element.type === 'activity') {
      const exec = this.calculateNextExecution(element.content || element);
      if (exec && !exec.isSameOrAfter(moment().startOf('week'))) return 'paused';
    } else if (element.type === 'gastronomy') {
      const items = element.children?.length ? element.children : [element];
      const useItem = items.some((item: any) => moment(item.content?.date).isSameOrAfter(moment().startOf('day')));
      if (!useItem) return 'paused';
    } else if (element.type === 'event') {
      if (!this.checkEventDate(element.content)) return 'paused';
    } else if (element.type === 'voucher' && element.content?.expirationDate) {
      if (moment(element.content.expirationDate).isSameOrBefore(moment().startOf('day'))) return 'paused';
    }

    return 'active';
  }

  public calculateNextExecution(content: any) {
    const todayMom = moment();
    if (content.repeating) {
      const mom = moment(content.date || undefined);
      switch (content.rhythm) {
        case 'weekly':
          while (!content.weekdays.includes(todayMom.weekday())) {
            todayMom.add(1, 'day');
          }
          break;
        case 'monthly':
          if (mom.date() < todayMom.date()) todayMom.add(1, 'month');
          todayMom.date(mom.date());
          break;
        case 'yearly':
          if (mom.isBefore(todayMom, 'day')) todayMom.add(1, 'year');
          todayMom.month(mom.month()).date(mom.date());
      }
      return todayMom;
    }
    return content.date ? moment(content.date) : null;
  }

  private checkEventDate(content: any): boolean {
    if (content?.repeating) {
      const today = new Date().setHours(0, 0, 0, 0);
      return this.getNextExecutionDateRepeating(content).some(d => d.getTime() >= today);
    }
    if (content?.validity && !content.multipleDates) {
      return this.checkValidDate(content.validity.start, content.validity.end);
    }
    if (content?.dates) {
      const dates = Array.isArray(content.dates) ? content.dates : [content.dates];
      if (!dates.length) return true;

      return (content.multipleDates || dates).some((date: any) => {
        if (date.daily === undefined) return false;
        const start = this.getDateUnix(date.startDate);
        const end = this.getDateUnix(date.endDate, true);
        return (!start && !end) || this.checkValidDate(start, end);
      });
    }
    return false;
  }

  getNextExecutionDateRepeating(event: any) {
    let endDate = event?.repetition?.endDate ? new Date(event.repetition.endDate) : null;
    if (!endDate) {
      endDate = new Date();
      endDate.setFullYear(endDate.getFullYear() + 1);
    }
    if (this.app?.detail?.publicationTimespan) {
      const d = new Date();
      if (this.app.detail.publicationTimespan.days) {
        d.setDate(d.getDate() + this.app.detail.publicationTimespan.days);
        endDate = d;
      } else if (this.app.detail.publicationTimespan.months != null) {
        d.setMonth(d.getMonth() + this.app.detail.publicationTimespan.months);
        endDate = new Date(d.getFullYear(), d.getMonth() + 1, 0);
      }
    }
    endDate.setHours(23, 59, 59, 999);

    const date = new Date(event.repetition.startDate);
    const nth = this.getWeekdayOccurrence(date);
    const day = date.getDay();
    const dates = [new Date(date)];

    if (event.repetition.repetitionSize === 'day') {
      const step = parseInt(event.repetition.repetitionRhythm, 10);
      if (event.repetition.repetitionRhythm === '1') {
        date.setDate(date.getDate() + 1);
        while (date.getTime() < endDate.getTime()) {
          const dayVal = date.getDay();
          if (event.repetition.weekdays.includes(dayVal.toString()) || event.repetition.weekdays.includes(date)) {
            dates.push(new Date(date));
          }
          date.setDate(date.getDate() + 1);
        }
      } else {
        date.setDate(date.getDate() + step);
        while (date.getTime() < endDate.getTime()) {
          dates.push(new Date(date));
          date.setDate(date.getDate() + step);
        }
      }
    } else if (event.repetition.repetitionSize === 'week') {
      const loopDate = this.getMonday(event.repetition.startDate);
      const startMs = new Date(event.repetition.startDate).getTime();
      const rhythmWeeks = (parseInt(event.repetition.repetitionRhythm, 10) - 1) * 7;

      while (loopDate.getTime() < endDate.getTime()) {
        for (let i = 0; i < 7; i++) {
          if (loopDate.getTime() < startMs) {
            loopDate.setDate(loopDate.getDate() + 1);
            continue;
          }
          if (event.repetition.weekdays.includes(loopDate.getDay().toString()) || event.repetition.weekdays.includes(loopDate.getDay())) {
            dates.push(new Date(loopDate));
          }
          loopDate.setDate(loopDate.getDate() + 1);
          if (i === 6) {
            loopDate.setDate(loopDate.getDate() + rhythmWeeks);
          }
        }
      }
    } else if (event.repetition.repetitionSize === 'month') {
      const step = parseInt(event.repetition.repetitionRhythm, 10);
      if (event.repetition.repetitionLastDay) {
        while (date.getTime() < endDate.getTime()) {
          date.setDate(1);
          date.setMonth(date.getMonth() + step + 1);
          date.setDate(0);
          dates.push(new Date(date));
        }
      } else if (event.repetition.repetitionExact) {
        while (date.getTime() < endDate.getTime()) {
          dates.push(new Date(date));
          date.setDate(date.getDate() + this.getDaysInMonth(date));
        }
      } else {
        let loopDate = new Date(date);
        while (loopDate.getTime() < endDate.getTime()) {
          dates.push(new Date(loopDate));
          loopDate = this.getNthWeekdayOfNextMonth(loopDate, day, nth);
        }
      }
    } else if (event.repetition.repetitionSize === 'year') {
      let loopDate = new Date(date);
      if (event.repetition.repetitionExact) {
        while (loopDate.getTime() < endDate.getTime()) {
          dates.push(new Date(loopDate));
          loopDate = new Date(loopDate.getFullYear() + 1, loopDate.getMonth(), loopDate.getDate());
        }
      } else {
        while (loopDate.getTime() < endDate.getTime()) {
          dates.push(new Date(loopDate));
          for (let i = 0; i < 12; i++) {
            loopDate = this.getNthWeekdayOfNextMonth(loopDate, day, nth);
          }
        }
      }
    }
    return dates;
  }

  getMonday(date: any) {
    const currentDate = new Date(date);
    const diff = (currentDate.getDay() === 0) ? -6 : 1 - currentDate.getDay();
    currentDate.setDate(currentDate.getDate() + diff);
    currentDate.setHours(0, 0, 0, 0);
    return currentDate;
  }

  getDaysInMonth(date: any) {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  }

  getWeekdayOccurrence(date: any) {
    const currentDate = new Date(date);
    currentDate.setDate(1);
    const firstOccurrence = 1 + ((date.getDay() - currentDate.getDay() + 7) % 7);
    return Math.floor((date.getDate() - firstOccurrence) / 7) + 1;
  }

  getNthWeekdayOfNextMonth(currentDate: any, targetWeekday: number, targetOccurrence: number) {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstOfNextMonth = new Date(year, month + 1, 1);
    const dayDiff = (targetWeekday - firstOfNextMonth.getDay() + 7) % 7;
    const dayOfMonth = (1 + dayDiff) + (targetOccurrence - 1) * 7;
    return new Date(year, month + 1, dayOfMonth);
  }

  private checkValidDate(startDate: any, endDate: any) {
    const limit = this.getNulledDate();
    return endDate ? limit <= endDate : limit <= startDate;
  }

  private getNulledDate() {
    return new Date().setHours(0, 0, 0, 0) / 1000;
  }

  private getDateUnix(dateString: any, isEndDate = false) {
    const res = this.getDate(dateString, isEndDate);
    return res ? res.getTime() / 1000 : null;
  }

  private getDate(dateString: any, isEndDate = false) {
    if (!dateString) return null;
    if (/\d?\d\.\d?\d\.(\d{2}|\d{4})/.test(dateString)) {
      const [d, m, y] = dateString.split('.');
      const res = new Date(`${m}/${d}/${y}`);
      if (isEndDate) res.setHours(23, 59, 59);
      return res;
    }
    return new Date(dateString);
  }

  private weekdaysValid(weekdays: any): boolean {
    if (weekdays == null) return true;
    const list = Array.isArray(weekdays) ? weekdays : Object.values(weekdays);
    return list.map(String).includes(String(new Date().getDay()));
  }

  private sameDay(d1: any, d2: any) {
    return !!(d1 && d2 && d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate());
  }

  private cleanUpDate(d: any) {
    if (typeof d === 'string' && /\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d.\d{3}/.test(d)) {
      d = new Date(d).getTime() / 1000;
    }
    if (typeof d === 'number') {
      d = new Date(d > 1e12 ? d : d * 1000);
    }
    return d;
  }
}
